import React, { useEffect } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Button,
  DropdownTrigger,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Chip,
  User,
  Pagination,
  Selection,
  ChipProps,
  SortDescriptor,
  useDisclosure,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { LuChevronDown } from "react-icons/lu";
import { IoSearchSharp } from "react-icons/io5";
import { AiOutlineUsergroupAdd } from "react-icons/ai";
import userImgActive from "../../user-active.png";
import userImgInactive from "../../user-inactive.png";

import { capitalize } from "../../utils/utils";
import { v4 as uuidv4 } from "uuid";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import AddUserModal from "../common/modal/AddUserModal";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import SecondaryActions from "./SecondaryActions";

const columns = [
  { name: "ID", uid: "user_id", sortable: true },
  { name: "NAME", uid: "user_name", sortable: true },
  { name: "EMAIL", uid: "user_email" },
  { name: "STATUS", uid: "availability", sortable: true },
  { name: "ACTIONS", uid: "actions" },
];

const availabilityOptions = [
  { name: "Active", uid: "active" },
  { name: "Inactive", uid: "inactive" },
];

const availabilityColorMap: Record<string, ChipProps["color"]> = {
  active: "success",
  inactive: "danger",
};

const INITIAL_VISIBLE_COLUMNS = ["user_name", "availability", "actions", "user_email"];

interface Column {
  uid: string;
  name: string;
  sortable?: boolean;
}

interface UserType {
  user_id: string;
  user_name: string;
  user_given_name: string;
  user_email: string;
  availability: string;
  [key: string]: any;
}

export default function Users() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();

  const { data: apiResponse, refetch } = useQuery<{ data: UserType[]; pagination: any }>({
    queryKey: ["AllUsers"],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/users`);
      return response.data;
    },
    staleTime: 30000,
    gcTime: 30000,
  });
  const users = React.useMemo(() => apiResponse?.data ?? [], [apiResponse]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("refresh") === "true") {
      refetch();
    }
  }, [location, refetch]);

  const [filterValue, setFilterValue] = React.useState("");
  const [selectedKeys, setSelectedKeys] = React.useState<Selection>(new Set([]));
  const [visibleColumns, setVisibleColumns] = React.useState<Selection>(
    new Set(INITIAL_VISIBLE_COLUMNS)
  );
  const [availabilityFilter, setAvailabilityFilter] = React.useState<Selection>("all");
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor>({
    column: "user_name",
    direction: "ascending",
  });

  const [page, setPage] = React.useState(1);

  const hasSearchFilter = Boolean(filterValue);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const headerColumns = React.useMemo(() => {
    if (visibleColumns === "all") return columns;

    return columns.filter((column) => Array.from(visibleColumns).includes(column.uid));
  }, [visibleColumns]);

  const filteredItems = React.useMemo(() => {
    let filteredUsers = [...users];

    if (hasSearchFilter) {
      filteredUsers = filteredUsers.filter(
        (user) =>
          user.user_name.toLowerCase().includes(filterValue.toLowerCase()) ||
          user.user_given_name.toLowerCase().includes(filterValue.toLowerCase())
      );
    }
    if (
      availabilityFilter !== "all" &&
      Array.from(availabilityFilter).length !== availabilityOptions.length
    ) {
      filteredUsers = filteredUsers.filter((user) =>
        Array.from(availabilityFilter).includes(user.availability)
      );
    }

    return filteredUsers;
  }, [filterValue, availabilityFilter, hasSearchFilter, users]);

  const pages = Math.ceil(filteredItems.length / rowsPerPage);

  const items = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;

    return filteredItems.slice(start, end);
  }, [page, filteredItems, rowsPerPage]);

  const sortedItems = React.useMemo(() => {
    return [...items].sort((a: UserType, b: UserType) => {
      const first = a[sortDescriptor.column as keyof UserType];
      const second = b[sortDescriptor.column as keyof UserType];

      let cmp = 0;
      if (typeof first === "number" && typeof second === "number") {
        cmp = first - second;
      } else if (typeof first === "string" && typeof second === "string") {
        cmp = first.localeCompare(second);
      }

      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [sortDescriptor, items]);

  const renderCell = React.useCallback(
    (user: UserType, columnKey: React.Key) => {
      const cellValue = user[columnKey as keyof UserType];

      switch (columnKey) {
        case "user_name":
          return (
            <NavLink to={`/users/${user.user_id}`}>
              <User
                avatarProps={{
                  radius: "lg",
                  src: user.availability === "active" ? userImgActive : userImgInactive,
                  classNames: { base: ["bg-white"] },
                }}
                description={user.user_email}
                name={`${user.user_name} ${user.user_given_name}`}
              >
                {user.user_email}
              </User>
            </NavLink>
          );
        case "availability":
          return (
            <Chip
              className="capitalize"
              color={availabilityColorMap[user.availability]}
              size="sm"
              variant="flat"
            >
              {t(cellValue)}
            </Chip>
          );
        case "actions":
          return (
            <div className="relative flex justify-end items-center gap-2">
              <SecondaryActions user_id={user.user_id} />
            </div>
          );
        default:
          return cellValue;
      }
    },
    [t]
  );

  const onRowsPerPageChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  }, []);

  const onSearchChange = React.useCallback((value?: string) => {
    if (value) {
      setFilterValue(value);
      setPage(1);
    } else {
      setFilterValue("");
    }
  }, []);

  const topContent = React.useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-3">
            <Input
              isClearable
              classNames={{
                base: "w-full min-w-[75%]",
                inputWrapper: [
                  "border-0",
                  "bg-white",
                  "h-9",
                  "min-h-unit-9",
                  "hover:bg-white",
                  "group-data-[hover=true]:bg-white",
                  "group-data-[focus=true]:bg-white",
                ],
                input: "text-vistagrey",
              }}
              placeholder={t("search")}
              size="md"
              startContent={<IoSearchSharp className="text-default-300" />}
              value={filterValue}
              variant="flat"
              radius="full"
              onClear={() => setFilterValue("")}
              onValueChange={onSearchChange}
              data-cy="headerSearchInput"
            />
            <div className="flex gap-3">
              <Dropdown>
                <DropdownTrigger className="hidden sm:flex">
                  <Button
                    endContent={<LuChevronDown className="text-small" />}
                    size="sm"
                    variant="flat"
                    radius="full"
                    className="h-9 bg-white text-vistagrey text-small"
                  >
                    {t("status")}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  disallowEmptySelection
                  aria-label={t("table_columns")}
                  closeOnSelect={false}
                  selectedKeys={availabilityFilter}
                  selectionMode="multiple"
                  onSelectionChange={setAvailabilityFilter}
                >
                  {availabilityOptions.map((availability) => (
                    <DropdownItem
                      key={availability.uid || uuidv4()}
                      textValue={availability.name || "N/A"}
                      className="capitalize"
                    >
                      {capitalize(t(availability.name))}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex gap-3">
              <Dropdown>
                <DropdownTrigger className="hidden sm:flex">
                  <Button
                    endContent={<LuChevronDown className="text-small" />}
                    size="sm"
                    variant="flat"
                    radius="full"
                    className="h-9 bg-white text-vistagrey text-small"
                  >
                    {t("columns")}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  disallowEmptySelection
                  aria-label={t("table_columns")}
                  closeOnSelect={false}
                  selectedKeys={visibleColumns}
                  selectionMode="multiple"
                  onSelectionChange={setVisibleColumns}
                >
                  {columns.map((column) => (
                    <DropdownItem key={column.uid} textValue={column.name} className="capitalize">
                      {capitalize(t(column.name))}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-default-400 text-small">
            {t("total_users", { count: users.length })}
          </span>
          <label className="flex items-center text-default-400 text-small">
            {t("users_per_page")} :{" "}
            <select
              className="bg-transparent outline-none text-default-400 text-small"
              onChange={onRowsPerPageChange}
            >
              <option value="15">15</option>
              <option value="50">50</option>
            </select>
          </label>
        </div>
      </div>
    );
  }, [
    filterValue,
    availabilityFilter,
    visibleColumns,
    onSearchChange,
    onRowsPerPageChange,
    users.length,
    t,
  ]);

  const bottomContent = React.useMemo(() => {
    return (
      <div className="py-2 px-2 flex justify-between items-center">
        <Pagination
          showControls
          isCompact
          page={page}
          total={pages}
          variant="light"
          onChange={setPage}
        />
        <Button type="button" onPress={onOpen} className="bg-vistagreen text-white rounded-md p-3">
          {t("add_user")}
        </Button>
      </div>
    );
  }, [page, pages, onOpen, t]);

  const classNames = React.useMemo(
    () => ({
      wrapper: ["max-h-[382px]"],
      table: ["border-separate", "border-spacing-y-2"],
      th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
      td: [
        "bg-white",
        "p-3",
        "group-data-[first=true]:first:before:rounded-none",
        "group-data-[first=true]:last:before:rounded-none",
        "group-data-[middle=true]:before:rounded-none",
        "group-data-[last=true]:first:before:rounded-none",
        "group-data-[last=true]:last:before:rounded-none",
      ],
      label: [
        "text-[10px] uppercase text-vistagrey/60 z-0 group-data-[filled=true]:text-vistagrey/60",
      ],
    }),
    []
  );

  return (
    <>
      <div className="w-full text-vistagrey my-6 inline-flex">
        <div>
          <div className="text-vistagrey text-xl">
            <AiOutlineUsergroupAdd className="fill-vistagreen inline-block text-4xl mr-2" />
            {t("users")}
          </div>
          <div className="text-sm pl-11 -mt-2">{t("user_management")}</div>
        </div>
      </div>

      <Table
        aria-label={t("users_list")}
        removeWrapper
        bottomContent={bottomContent}
        bottomContentPlacement="outside"
        classNames={classNames}
        selectedKeys={selectedKeys}
        sortDescriptor={sortDescriptor}
        topContent={topContent}
        topContentPlacement="outside"
        onSelectionChange={setSelectedKeys}
        onSortChange={setSortDescriptor}
        selectionMode="single"
      >
        <TableHeader<Column>
          columns={headerColumns}
          children={(column) => (
            <TableColumn
              key={column.uid}
              align={column.uid === "actions" ? "center" : "start"}
              allowsSorting={column.sortable}
              children={t(column.name)}
            />
          )}
        />

        <TableBody<UserType>
          emptyContent={t("no_users_found")}
          items={sortedItems}
          children={(item) => (
            <TableRow
              key={item.user_id}
              onClick={() => navigate(`/users/${item.user_id}`)}
              className="cursor-pointer"
              children={(columnKey) => <TableCell children={renderCell(item, columnKey)} />}
            />
          )}
        />
      </Table>

      <AddUserModal isOpen={isOpen} onClose={onClose} classNames={classNames} />
    </>
  );
}
